import React from 'react';
import {
    Card,
    CardContent,
    LinearProgress,
    Typography
} from '@mui/material';
import { BudgetUsage } from '../../utils/budgetUtils';
import { format } from 'date-fns';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';

interface Props {
    category: string;
    usage: BudgetUsage;
    totalBudget: number;
    startDate: Date;
    endDate?: Date;
}

const BudgetSummaryCard: React.FC<Props> = ({
    category,
    usage,
    totalBudget,
    startDate,
    endDate,
}) => {
    const formatting = useFormatting();

    return (
        <Card
            elevation={0}
            sx={{
                minWidth: 250,
                flex: 1,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'rgba(25, 118, 210, 0.03)',
                boxShadow: 'none',
            }}
        >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {category} Budget
                </Typography>

                <Typography variant="body2">
                    Spent: {formatMoney(usage.spent, formatting)} / {formatMoney(totalBudget, formatting)}
                </Typography>

                <LinearProgress
                    variant="determinate"
                    value={Math.min(usage.percentUsed, 100)}
                    sx={{ height: 10, borderRadius: 5, my: 1, backgroundColor: '#eee' }}
                />

                <Typography variant="caption" color="text.secondary" display="block">
                    Remaining: {formatMoney(usage.remaining, formatting)} ({usage.percentUsed.toFixed(1)}%)
                </Typography>

                <Typography variant="caption" color="text.secondary">
                    {format(startDate, 'MMM d, yyyy')} –{' '}
                    {endDate ? format(endDate, 'MMM d, yyyy') : 'Ongoing'}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default BudgetSummaryCard;
